import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Calendar, Clock, MapPin, Trophy, Users, ArrowLeft, Play, Edit2, IdCard } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import FeaturedLayout from '../layouts/FeaturedLayout';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '../ui';
import { getAsistenciasByJuego, getJuegoById, getGruposAsignadosByTorneo, updateJuego } from '../../lib/supabase';
import { parseLocalDate } from '../../lib/dateUtils';
import { cn, fixMojibake } from '../../lib/utils';
import JuegoScoreModal from './JuegoScoreModal';
import FormJuego from './FormJuego';
import { useOrganization } from '../../contexts/OrganizationContext';
import { puedeEditarJuego } from '../../lib/torneoOrigen';

const RESULTADO_STYLES = {
  W: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  L: 'bg-rose-50 text-rose-700 border-rose-200',
  D: 'bg-slate-50 text-slate-700 border-slate-200'
};

const RESULTADO_LABELS = {
  W: 'Victoria',
  L: 'Derrota',
  D: 'Empate'
};

const ESTATUS_LABELS = {
  programado: 'Programado',
  en_curso: 'En curso',
  terminado: 'Terminado',
  cancelado: 'Cancelado',
  pospuesto: 'Pospuesto'
};

const JuegoDetailView = () => {
  const { juegoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { org } = useOrganization();
  const [juego, setJuego] = useState(null);
  const [asistencias, setAsistencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAsistencias, setLoadingAsistencias] = useState(true);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [gruposTorneo, setGruposTorneo] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  useEffect(() => {
    let isActive = true;
    const loadJuego = async () => {
      if (!juegoId) return;
      setLoading(true);
      try {
        const data = await getJuegoById(juegoId);
        if (isActive) setJuego(data);
      } catch (error) {
        console.error('Error cargando juego:', error);
        if (isActive) setJuego(null);
      } finally {
        if (isActive) setLoading(false);
      }
    };
    loadJuego();
    return () => {
      isActive = false;
    };
  }, [juegoId]);

  useEffect(() => {
    let isActive = true;
    const loadAsistencias = async () => {
      if (!juegoId) return;
      setLoadingAsistencias(true);
      try {
        const data = await getAsistenciasByJuego(juegoId);
        if (isActive) setAsistencias(data || []);
      } catch (error) {
        console.error('Error cargando asistencias del juego:', error);
        if (isActive) setAsistencias([]);
      } finally {
        if (isActive) setLoadingAsistencias(false);
      }
    };
    loadAsistencias();
    return () => {
      isActive = false;
    };
  }, [juegoId]);

  // El partido es del club: ni viene del rol oficial de otra org, ni es de un torneo ajeno.
  const esMio = puedeEditarJuego(juego, org?.id);

  // MisJuegos manda { focus: 'editar' } al navegar aqui, y nadie lo leia: el foco se
  // perdia y el boton "Editar juego" caia en una ruta que solo redirige. Se exige juego
  // cargado y esMio para que un enlace viejo o manipulado no abra el modal en un juego
  // ajeno -- la misma guardia que ya protege al boton.
  //
  // El foco se consume UNA vez: guardar reemplaza el objeto `juego`, el efecto corre de
  // nuevo y, con el state todavia puesto, el modal se reabria solo. Se limpia igual que
  // en MisJuegos, con navigate(..., { state: null }).
  useEffect(() => {
    if (location.state?.focus !== 'editar' || !juego || !esMio) return;
    setEditError(null);
    setShowEditForm(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, juego, esMio, navigate]);

  useEffect(() => {
    if (!showEditForm || !juego?.torneo_id) return;
    let vivo = true;
    (async () => {
      const { data } = await getGruposAsignadosByTorneo(juego.torneo_id);
      if (vivo) setGruposTorneo(data || []);
    })();
    return () => { vivo = false; };
  }, [showEditForm, juego?.torneo_id]);

  const asistenciaStats = useMemo(() => {
    const stats = { presente: 0, retardo: 0, ausente: 0, exento: 0 };
    asistencias.forEach((item) => {
      const key = (item.presente || '').toLowerCase();
      if (stats[key] !== undefined) stats[key] += 1;
    });
    const total = stats.presente + stats.retardo + stats.ausente;
    return { ...stats, total };
  }, [asistencias]);

  const grupoNombre = fixMojibake(juego?.grupo?.nombre_del_grupo || 'Grupo');
  const equipoNombre = fixMojibake(juego?.equipo?.nombre || juego?.equipo_nombre || 'Equipo por definir');
  const torneoNombre = fixMojibake(juego?.torneo?.nombre || '');

  const fechaLabel = useMemo(() => {
    if (!juego?.fecha) return '--';
    const parsed = parseLocalDate(juego.fecha);
    return parsed ? format(parsed, "d 'de' MMMM yyyy", { locale: es }) : juego.fecha;
  }, [juego?.fecha]);

  const horaLabel = juego?.hora ? juego.hora.substring(0, 5) : '--:--';
  const canEditResultado = (juego?.estatus || '').toLowerCase() === 'terminado';

  if (loading) {
    return (
      <FeaturedLayout title="Juego" subtitle="Detalle del juego">
        <div className="p-6 text-sm text-muted-foreground">Cargando juego...</div>
      </FeaturedLayout>
    );
  }

  if (!juego) {
    return (
      <FeaturedLayout title="Juego" subtitle="Detalle del juego">
        <div className="p-6 text-sm text-muted-foreground">No pudimos cargar este juego.</div>
      </FeaturedLayout>
    );
  }

  return (
    <FeaturedLayout
      title="Detalle del juego"
      subtitle={`${grupoNombre} vs ${equipoNombre}${juego?.condicion === 'visita' ? ' · Visita' : juego?.condicion === 'local' ? ' · En casa' : ''}`}
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="ghost" className="flex items-center gap-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            {/* Partido de la red: las credenciales del rival (spec 2026-10-15). La base decide
                si ya se abren; el boton sale siempre. */}
            {juego.torneo_juego_id && (
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => navigate(`/torneos/juegos/${juego.id}/rival`)}
              >
                <IdCard className="h-4 w-4" />
                Credenciales del rival
              </Button>
            )}
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => navigate(`/attendance/juego/${juego.grupo_id}/${juego.id}`)}
            >
              <Play className="h-4 w-4" />
              Tomar asistencia
            </Button>
            {/* La asistencia siempre es del club, tambien en un torneo externo. El
                marcador y el partido no: los pone la organizadora y bajan solos. */}
            {esMio && (
              <>
                <Button
                  variant="outline"
                  className="flex items-center gap-2"
                  disabled={!canEditResultado}
                  onClick={() => setShowScoreModal(true)}
                >
                  Editar marcador
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center gap-2"
                  onClick={() => {
                    setEditError(null);
                    setShowEditForm(true);
                  }}
                >
                  <Edit2 className="h-4 w-4" />
                  Editar juego
                </Button>
              </>
            )}
          </div>
        </div>

        <Card className="border shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{fixMojibake(juego.nombre) || `vs ${equipoNombre}`}</CardTitle>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline">
                {ESTATUS_LABELS[juego.estatus] || juego.estatus || 'Programado'}
              </Badge>
              {juego.fase && <Badge variant="secondary">{fixMojibake(juego.fase)}</Badge>}
              {juego.jornada && <Badge variant="outline">Jornada {juego.jornada}</Badge>}
              {torneoNombre && <Badge variant="outline">{torneoNombre}</Badge>}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                {fechaLabel}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                {horaLabel}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-4 w-4" />
                {grupoNombre}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Trophy className="h-4 w-4 text-amber-500" />
                vs {equipoNombre}
              </div>
              {juego.ubicacion && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground sm:col-span-2">
                  <MapPin className="h-4 w-4" />
                  {fixMojibake(juego.ubicacion)}
                  {juego.cancha ? ` · ${fixMojibake(juego.cancha)}` : ''}
                  {juego.campo_numero ? ` · Campo ${juego.campo_numero}` : ''}
                </div>
              )}
            </div>

            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">Marcador</h4>
                {juego.resultado && (
                  <Badge variant="outline" className={cn('text-xs', RESULTADO_STYLES[juego.resultado])}>
                    {RESULTADO_LABELS[juego.resultado] || juego.resultado}
                  </Badge>
                )}
              </div>
              {juego.puntos_favor !== null && juego.puntos_contra !== null ? (
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-2xl font-semibold">{juego.puntos_favor}</p>
                    <p className="text-xs text-muted-foreground">{grupoNombre}</p>
                  </div>
                  <div className="text-sm text-muted-foreground">vs</div>
                  <div>
                    <p className="text-2xl font-semibold">{juego.puntos_contra}</p>
                    <p className="text-xs text-muted-foreground">{equipoNombre}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Aun no hay marcador registrado.</p>
              )}
            </div>

            <div className="rounded-lg border p-4 space-y-2">
              <h4 className="text-sm font-semibold">Comentarios</h4>
              {juego.comentarios ? (
                <p className="text-sm text-muted-foreground">{juego.comentarios}</p>
              ) : (
                <p className="text-sm text-muted-foreground">Sin comentarios registrados.</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Asistencia</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-5 text-sm">
              <div className="rounded-lg border bg-emerald-50 px-3 py-2 text-emerald-700">
                <div className="text-[11px] uppercase">Presentes</div>
                <div className="text-lg font-semibold">{asistenciaStats.presente}</div>
              </div>
              <div className="rounded-lg border bg-amber-50 px-3 py-2 text-amber-700">
                <div className="text-[11px] uppercase">Retardos</div>
                <div className="text-lg font-semibold">{asistenciaStats.retardo}</div>
              </div>
              <div className="rounded-lg border bg-rose-50 px-3 py-2 text-rose-700">
                <div className="text-[11px] uppercase">Ausentes</div>
                <div className="text-lg font-semibold">{asistenciaStats.ausente}</div>
              </div>
              <div className="rounded-lg border bg-slate-100 px-3 py-2 text-slate-700">
                <div className="text-[11px] uppercase">Exentos</div>
                <div className="text-lg font-semibold">{asistenciaStats.exento}</div>
              </div>
              <div className="rounded-lg border bg-slate-50 px-3 py-2 text-slate-700">
                <div className="text-[11px] uppercase">Registros</div>
                <div className="text-lg font-semibold">{asistenciaStats.total}</div>
              </div>
            </div>

            {loadingAsistencias ? (
              <p className="text-sm text-muted-foreground">Cargando asistencia...</p>
            ) : asistencias.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aun no hay asistencias registradas.</p>
            ) : (
              <div className="text-xs text-muted-foreground">
                {asistencias.length} registros encontrados.
              </div>
            )}
          </CardContent>
        </Card>

        <JuegoScoreModal
          isOpen={showScoreModal}
          juego={juego}
          onClose={() => setShowScoreModal(false)}
          onSaved={(updated) => setJuego((prev) => ({ ...(prev || {}), ...updated }))}
        />

        {/* Igual de protegido que el boton que lo abre: un juego ajeno (rol oficial o
            torneo externo) nunca muestra este modal, ni siquiera via location.state. */}
        {esMio && showEditForm && juego && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-background rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold">Editar juego</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowEditForm(false)}>✕</Button>
              </div>
              <div className="p-4">
                <FormJuego
                  juego={juego}
                  torneo={juego.torneo}
                  gruposTorneo={gruposTorneo}
                  saving={savingEdit}
                  externalError={editError}
                  onCancel={() => setShowEditForm(false)}
                  onSubmit={async (datos) => {
                    setSavingEdit(true);
                    setEditError(null);
                    try {
                      await updateJuego(juego.id, datos);
                      setShowEditForm(false);
                      // Ojo: getJuegoById devuelve el juego directo y LANZA si falla --
                      // no sigue el patron { data, error } del resto de los servicios.
                      try {
                        setJuego(await getJuegoById(juegoId));
                      } catch (e) {
                        console.error('Error recargando el juego:', e);
                      }
                    } catch (err) {
                      console.error('Error actualizando el juego:', err);
                      setEditError(err.message || 'No se pudo actualizar el juego.');
                    } finally {
                      setSavingEdit(false);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </FeaturedLayout>
  );
};

export default JuegoDetailView;


